import { useEffect, useState } from "react";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useUser } from "./useHook";
import Swal from "sweetalert2";
import Loader, { ImageWithFallback } from "../../Utils/loader";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";

function Users() {
  const { getAllUsers, updateUserStatus, searchUsers } = useUser();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [usersData, setUsersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingUserId, setLoadingUserId] = useState(null); // Track which user is being updated

  // Pagination state
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 9
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);

  const handleSearch = async () => {
    try {
      const response = await searchUsers(searchQuery, emailQuery, statusFilter, offset, limit);
      setUsersData(response.data);
      setPagination({
        totalRecords: response.pagination.totalRecords,
        totalPages: response.pagination.totalPages,
        currentPage: response.pagination.currentPage,
        perPage: response.pagination.perPage
      });
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleReset = () => {
    setSearchQuery("");
    setEmailQuery("");
    setStatusFilter("");
    setOffset(0);
    getAllUsers(setUsersData, 0, limit);
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await getAllUsers(setUsersData, offset, limit);
        if (response.pagination) {
          setPagination({
            totalRecords: response.pagination.totalRecords,
            totalPages: response.pagination.totalPages,
            currentPage: response.pagination.currentPage,
            perPage: response.pagination.perPage
          });
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [offset]);

  const handleShowDetails = (user) => {
    navigate("/users/view", { state: user });
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setLoadingUserId(id); // Set loading for specific user
    try {
      const newStatus = !currentStatus;
      const response = await updateUserStatus(id, newStatus);

      if (response) {
        setUsersData((prevUser) =>
          prevUser.map((user) =>
            user.id === id ? { ...user, status: newStatus } : user
          )
        );

        Swal.fire({
          title: "Success",
          text: `User status has been ${newStatus ? "activated" : "deactivated"
            } successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update user status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setLoadingUserId(null); // Clear loading state
    }
  };

  // Show loader while loading initially
  if (isLoading) {
    return <Loader message="Loading users..." />;
  }

  return (
    <div>
      <div className="bg-gray-100 p-4 flex justify-between">
        <h2 className="text-2xl font-bold">Users</h2>
      </div>

      <div className="mt-4">
        <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
          <input
            type="text"
            className="w-[30%] px-4 py-2 border rounded-lg"
            placeholder="Search by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <input
            type="email"
            className="w-[30%] px-4 py-2 border rounded-lg"
            placeholder="Search by email"
            value={emailQuery}
            onChange={(e) => setEmailQuery(e.target.value)}
          />
          <select
            className="w-[20%] px-4 py-2 border rounded-lg"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="" className="opacity-25">
              Select Status
            </option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={handleSearch}
          >
            Search
          </button>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white shadow-md rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Sr. No.
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {usersData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    No users found.
                  </td>
                </tr>
              ) : (
                usersData.map((user, index) => (
                  <tr
                    key={user.id}
                    className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {offset + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <ImageWithFallback
                        src={user.profileImage}
                        alt={user.firstName}
                        className="w-14 h-14 object-cover rounded-full shadow-lg"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {user.email}
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className={`relative inline-block w-16 h-8 rounded-full transition-colors duration-300 cursor-pointer ${user.status ? "bg-primary-dark" : "bg-[#cccccc]"
                          } ${loadingUserId === user.id ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={() => loadingUserId !== user.id && handleToggleStatus(user.id, user.status)}
                      >
                        <div
                          className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${user.status ? "translate-x-8" : ""
                            }`}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <button
                          className="bg-blue-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center justify-center hover:bg-blue-600 transition-colors duration-200"
                          onClick={() => handleShowDetails(user)}
                        >
                          <IoEye className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Component */}
        {pagination.totalPages > 1 && (
          <div className="mt-6">
            <GlobalPagination
              offset={offset}
              limit={limit}
              total={pagination.totalRecords}
              onPageChange={handlePageChange}
              className="mt-4"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;